import axios from 'axios';
import React, { useState, useEffect, useRef } from 'react';
import { apiClient, showToast } from '../../utils/apiClient';
import { API } from '../../config';
import { formatDate, formatDateTime, formatNotesWithBullets, getDayOfWeek, formatDateWithDay, getLocationDisplay, getLocationChipColor, getRowAlert, formatDatetimeForInput, parseUTCDatetime, getTodayInAEST, isPrepaymentApproaching, hasAppointments, getAppointmentSummary } from '../../utils/helpers';
import { getViewingRoomOptions } from '../../utils/pricingHelpers';
import { useAuth } from '../../context/AuthContext';


const AddAppointmentModal = ({ onClose, onSuccess, prefilledDatetime = '' }) => {
  const { user } = useAuth();
  const [category, setCategory] = useState('existing'); // 'existing' or 'external'
  const [step, setStep] = useState(1); // 1 = select category/pet, 2 = select type & datetime
  const [activePets, setActivePets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  // Existing pet appointment data
  const [selectedPetId, setSelectedPetId] = useState('');
  const [appointmentType, setAppointmentType] = useState('ashes_collection');
  const [appointmentDatetime, setAppointmentDatetime] = useState(prefilledDatetime);
  const [viewingDuration, setViewingDuration] = useState('30 minutes');
  
  // Update datetime if prefilled value changes
  useEffect(() => {
    if (prefilledDatetime) {
      setAppointmentDatetime(prefilledDatetime);
    }
  }, [prefilledDatetime]);
  
  // External appointment data
  const [externalData, setExternalData] = useState({
    contact_name: '',
    phone: '',
    pet_name: '',
    appointment_type: 'Showroom Visit',
    other_type_description: '',
    notes: ''
  });

  useEffect(() => {
    fetchActivePets();
  }, []);

  const fetchActivePets = async () => {
    try {
      const response = await apiClient.get('/pets');
      const active = response.data.filter(p => p.status !== 'Completed');
      setActivePets(active);
    } catch (error) {
      console.error('Failed to fetch pets:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      
      if (category === 'existing') {
        // Save appointment to existing pet record
        const updateData = {};
        
        if (appointmentType === 'ashes_collection') {
          updateData.ashes_collection_datetime = appointmentDatetime;
          updateData.ashes_collection_booked = true;
          updateData.ashes_collection_notes = externalData.notes;  // Save to specific field
        } else if (appointmentType === 'prepayment') {
          updateData.prepayment_date = appointmentDatetime; // Full datetime for prepayments with time
          updateData.prepayment_notes = externalData.notes;  // Save to specific field
        } else if (appointmentType === 'viewing_room') {
          updateData.viewing_room_datetime = appointmentDatetime;
          updateData.viewing_room = viewingDuration;
          updateData.viewing_room_notes = externalData.notes;  // Save to specific field
        } else if (appointmentType === 'showroom_visit') {
          updateData.showroom_visit_datetime = appointmentDatetime;
          updateData.showroom_visit_notes = externalData.notes;  // Save to specific field
        } else if (appointmentType === 'other_appointment') {
          updateData.other_appointment_datetime = appointmentDatetime;
          updateData.other_appointment_notes = externalData.notes;
        } else if (appointmentType === 'pet_delivery') {
          updateData.pet_delivery_datetime = appointmentDatetime;
          updateData.pet_delivery_notes = externalData.notes;
        } else if (appointmentType === 'pet_dropoff') {
          updateData.pet_dropoff_datetime = appointmentDatetime;
          updateData.pet_dropoff_notes = externalData.notes;
        } else if (appointmentType === 'home_collection') {
          updateData.home_collection_datetime = appointmentDatetime;
          updateData.home_collection_notes = externalData.notes;
        } else if (appointmentType === 'vet_dropoff') {
          updateData.vet_dropoff_datetime = appointmentDatetime;
          updateData.vet_dropoff_notes = externalData.notes;
        } else if (appointmentType === 'euthanasia') {
          updateData.euthanasia_datetime = appointmentDatetime;
          updateData.euthanasia_notes = externalData.notes;
          updateData.euthanasia_service = true;
        }
        
        await apiClient.put(`/pets/${selectedPetId}`, updateData);
      } else {
        // Create external appointment
        const externalAppointment = {
          ...externalData,
          datetime: appointmentDatetime,
          created_by: user?.id
        };
        
        await apiClient.post('/external-appointments', externalAppointment);
      }
      
      showToast('Appointment scheduled successfully! ✅');
      onSuccess();
      onClose();
      
    } catch (error) {
      console.error('Failed to save appointment:', error);
      showToast('Failed to schedule appointment. Please try again.', 'error');
    } finally {
      setSaving(false);
    }
  };

  const selectedPet = activePets.find(p => p.id === selectedPetId);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-75 z-[70] flex items-center justify-center p-4">
      <div className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="bg-gradient-to-r from-purple-600 to-teal-600 text-white p-6 rounded-t-xl">
          <div className="flex justify-between items-center">
            <h2 className="text-2xl font-bold">+ Add Appointment</h2>
            <button
              onClick={onClose}
              className="text-white hover:text-gray-200 text-3xl font-bold"
            >
              ×
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-6">
          {loading ? (
            <div className="text-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto"></div>
              <p className="text-gray-600 mt-4">Loading pets...</p>
            </div>
          ) : (
            <>
              {/* Step 1: Select Category & Pet/Contact */}
              {step === 1 && (
                <div className="space-y-4">
                  {/* Category Selection */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Appointment Category
                    </label>
                    <div className="grid grid-cols-2 gap-3">
                      <button
                        onClick={() => setCategory('existing')}
                        className={`p-4 rounded-lg border-2 text-center ${
                          category === 'existing'
                            ? 'bg-purple-50 border-purple-500 text-purple-800'
                            : 'bg-gray-50 border-gray-300 text-gray-600'
                        }`}
                      >
                        <div className="text-2xl mb-1">🐾</div>
                        <div className="text-sm font-medium">Existing Pet</div>
                        <div className="text-xs text-gray-500">From our system</div>
                      </button>
                      <button
                        onClick={() => setCategory('external')}
                        className={`p-4 rounded-lg border-2 text-center ${
                          category === 'external'
                            ? 'bg-gray-100 border-gray-500 text-gray-800'
                            : 'bg-gray-50 border-gray-300 text-gray-600'
                        }`}
                      >
                        <div className="text-2xl mb-1">👫</div>
                        <div className="text-sm font-medium">External Visit</div>
                        <div className="text-xs text-gray-500">New/Walk-in</div>
                      </button>
                    </div>
                  </div>
                  
                  {/* Existing Pet Selection */}
                  {category === 'existing' && (
                    <>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Select Pet (Active Only)
                        </label>
                        <select
                          value={selectedPetId}
                          onChange={(e) => setSelectedPetId(e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                        >
                          <option value="">-- Select a pet --</option>
                          {activePets.map(pet => (
                            <option key={pet.id} value={pet.id}>
                              {pet.pet_name} - {pet.client_name} ({pet.status})
                            </option>
                          ))}
                        </select>
                        <p className="text-xs text-gray-500 mt-1">
                          Showing {activePets.length} active pets (completed pets excluded)
                        </p>
                      </div>
                      
                      {selectedPet && (
                        <div className="bg-gray-50 rounded-lg p-4">
                          <p className="text-sm text-gray-600 mb-1">Selected Pet</p>
                          <p className="font-bold text-gray-900">{selectedPet.pet_name}</p>
                          <p className="text-sm text-gray-600">{selectedPet.client_name}</p>
                          <p className="text-xs text-gray-500">Phone: {selectedPet.phone_number}</p>
                        </div>
                      )}
                    </>
                  )}
                  
                  {/* External Appointment Form */}
                  {category === 'external' && (
                    <>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Contact Name *
                        </label>
                        <input
                          type="text"
                          value={externalData.contact_name}
                          onChange={(e) => setExternalData({...externalData, contact_name: e.target.value})}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-500"
                          placeholder="e.g., Sarah Johnson"
                          required
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Phone Number *
                        </label>
                        <input
                          type="tel"
                          value={externalData.phone}
                          onChange={(e) => setExternalData({...externalData, phone: e.target.value})}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-500"
                          placeholder="e.g., 0412345678"
                          required
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Pet Name (Optional)
                        </label>
                        <input
                          type="text"
                          value={externalData.pet_name}
                          onChange={(e) => setExternalData({...externalData, pet_name: e.target.value})}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-500"
                          placeholder="e.g., Fluffy (if known)"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Appointment Type *
                        </label>
                        <select
                          value={externalData.appointment_type}
                          onChange={(e) => setExternalData({...externalData, appointment_type: e.target.value})}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-500"
                        >
                          <option value="Showroom Visit">Showroom Visit</option>
                          <option value="Client Drop-off">Client Drop-off</option>
                          <option value="Mobile Vet Drop-off">Mobile Vet Drop-off</option>
                          <option value="Vet Collection">🚐 Vet Collection</option>
                        </select>
                      </div>
                      {externalData.appointment_type === 'Vet Collection' && (
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Describe Appointment Type
                          </label>
                          <input
                            type="text"
                            value={externalData.other_type_description}
                            onChange={(e) => setExternalData({...externalData, other_type_description: e.target.value})}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-500"
                            placeholder="e.g., Product inquiry"
                          />
                        </div>
                      )}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Date & Time *
                        </label>
                        <input
                          type="datetime-local"
                          value={appointmentDatetime}
                          onChange={(e) => setAppointmentDatetime(e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-500"
                          required
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Notes (Optional)
                        </label>
                        <textarea
                          value={externalData.notes}
                          onChange={(e) => setExternalData({...externalData, notes: e.target.value})}
                          rows={2}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-500"
                          placeholder="Any additional details..."
                        />
                      </div>
                    </>
                  )}
                  
                  {/* Action Buttons - Outside of conditionals */}
                  <div className="flex justify-end space-x-3">
                    <button
                      onClick={onClose}
                      className="px-6 py-2 bg-gray-200 hover:bg-gray-300 text-gray-700 rounded-lg font-medium"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={() => {
                        if (category === 'existing') {
                          if (!selectedPetId) {
                            showToast('Please select a pet', 'error');
                            return;
                          }
                          setStep(2);
                        } else {
                          if (!externalData.contact_name || !externalData.phone || !appointmentDatetime) {
                            showToast('Please fill in required fields', 'error');
                            return;
                          }
                          handleSave();
                        }
                      }}
                      disabled={category === 'existing' ? !selectedPetId : (!externalData.contact_name || !externalData.phone || !appointmentDatetime)}
                      className="px-6 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {category === 'existing' ? 'Next: Set Appointment →' : (saving ? 'Saving...' : 'Schedule Appointment')}
                    </button>
                  </div>
                </div>
              )}
              
              {/* Step 2: Select Type & Datetime (Only for Existing Pets) */}
              {step === 2 && category === 'existing' && selectedPet && (
                <div className="space-y-4">
                  {/* Selected Pet Info */}
                  <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                    <p className="text-sm text-purple-800 font-medium">Scheduling for:</p>
                    <p className="font-bold text-gray-900 text-lg">{selectedPet.pet_name} - {selectedPet.client_name}</p>
                  </div>
                  
                  {/* Appointment Type */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Appointment Type
                    </label>
                    <div className="grid grid-cols-2 gap-2">
                      <button
                        onClick={() => setAppointmentType('ashes_collection')}
                        className={`p-3 rounded-lg border-2 text-center ${
                          appointmentType === 'ashes_collection'
                            ? 'bg-blue-50 border-blue-500 text-blue-800'
                            : 'bg-gray-50 border-gray-300 text-gray-600'
                        }`}
                      >
                        <div className="text-2xl mb-1">🏺</div>
                        <div className="text-xs font-medium">Ashes Collection</div>
                      </button>
                      <button
                        onClick={() => setAppointmentType('prepayment')}
                        className={`p-3 rounded-lg border-2 text-center ${
                          appointmentType === 'prepayment'
                            ? 'bg-orange-50 border-orange-500 text-orange-800'
                            : 'bg-gray-50 border-gray-300 text-gray-600'
                        }`}
                      >
                        <div className="text-2xl mb-1">⭐️</div>
                        <div className="text-xs font-medium">Prepayment</div>
                      </button>
                      <button
                        onClick={() => setAppointmentType('viewing_room')}
                        className={`p-3 rounded-lg border-2 text-center ${
                          appointmentType === 'viewing_room'
                            ? 'bg-purple-50 border-purple-500 text-purple-800'
                            : 'bg-gray-50 border-gray-300 text-gray-600'
                        }`}
                      >
                        <div className="text-2xl mb-1">🕯️</div>
                        <div className="text-xs font-medium">Viewing Room</div>
                      </button>
                      <button
                        onClick={() => setAppointmentType('showroom_visit')}
                        className={`p-3 rounded-lg border-2 text-center ${
                          appointmentType === 'showroom_visit'
                            ? 'bg-teal-50 border-teal-500 text-teal-800'
                            : 'bg-gray-50 border-gray-300 text-gray-600'
                        }`}
                      >
                        <div className="text-2xl mb-1">🚙</div>
                        <div className="text-xs font-medium">Showroom Visit</div>
                      </button>
                      <button
                        onClick={() => setAppointmentType('other_appointment')}
                        className={`p-3 rounded-lg border-2 text-center ${
                          appointmentType === 'other_appointment'
                            ? 'bg-pink-50 border-pink-500 text-pink-800'
                            : 'bg-gray-50 border-gray-300 text-gray-600'
                        }`}
                      >
                        <div className="text-2xl mb-1">🚐</div>
                        <div className="text-xs font-medium">Vet Collection</div>
                      </button>
                      <button
                        onClick={() => setAppointmentType('pet_delivery')}
                        className={`p-3 rounded-lg border-2 text-center ${
                          appointmentType === 'pet_delivery'
                            ? 'bg-green-50 border-green-500 text-green-800'
                            : 'bg-gray-50 border-gray-300 text-gray-600'
                        }`}
                      >
                        <div className="text-2xl mb-1">🚚</div>
                        <div className="text-xs font-medium">Ashes Delivery</div>
                      </button>
                      <button
                        type="button"
                        onClick={() => setAppointmentType('pet_dropoff')}
                        className={`p-3 rounded-lg border-2 text-center ${
                          appointmentType === 'pet_dropoff'
                            ? 'bg-indigo-50 border-indigo-500 text-indigo-800'
                            : 'bg-gray-50 border-gray-300 text-gray-600'
                        }`}
                      >
                        <div className="text-2xl mb-1">🦮</div>
                        <div className="text-xs font-medium">Client Drop off</div>
                      </button>
                      <button
                        type="button"
                        onClick={() => setAppointmentType('home_collection')}
                        className={`p-3 rounded-lg border-2 text-center ${
                          appointmentType === 'home_collection'
                            ? 'bg-emerald-50 border-emerald-500 text-emerald-800'
                            : 'bg-gray-50 border-gray-300 text-gray-600'
                        }`}
                      >
                        <div className="text-2xl mb-1">🏡</div>
                        <div className="text-xs font-medium">Home Collection</div>
                      </button>
                      <button
                        type="button"
                        onClick={() => setAppointmentType('vet_dropoff')}
                        className={`p-3 rounded-lg border-2 text-center ${
                          appointmentType === 'vet_dropoff'
                            ? 'bg-orange-50 border-orange-500 text-orange-800'
                            : 'bg-gray-50 border-gray-300 text-gray-600'
                        }`}
                      >
                        <div className="text-2xl mb-1">🏥</div>
                        <div className="text-xs font-medium">Vet Drop off</div>
                      </button>
                      <button
                        type="button"
                        onClick={() => setAppointmentType('euthanasia')}
                        className={`p-3 rounded-lg border-2 text-center ${
                          appointmentType === 'euthanasia'
                            ? 'bg-rose-50 border-rose-500 text-rose-800'
                            : 'bg-gray-50 border-gray-300 text-gray-600'
                        }`}
                      >
                        <div className="text-2xl mb-1">🕊️</div>
                        <div className="text-xs font-medium">Euthanasia</div>
                      </button>
                    </div>
                  </div>
                  
                  {/* Viewing Room Duration (only if viewing room selected) */}
                  {appointmentType === 'viewing_room' && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Duration
                      </label>
                      <select
                        value={viewingDuration}
                        onChange={(e) => setViewingDuration(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                      >
                        {getViewingRoomOptions().map(vr => (
                          <option key={vr.name} value={vr.name}>{vr.name} - ${vr.price}</option>
                        ))}
                      </select>
                    </div>
                  )}
                  
                  {/* Date & Time */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {appointmentType === 'prepayment' ? 'Pickup Date' : 'Appointment Date & Time'}
                    </label>
                    <input
                      type={appointmentType === 'prepayment' ? 'date' : 'datetime-local'}
                      value={appointmentDatetime}
                      onChange={(e) => setAppointmentDatetime(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                      required
                    />
                  </div>
                  
                  {/* Notes Field */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Notes (Optional)
                    </label>
                    <textarea
                      value={externalData.notes}
                      onChange={(e) => setExternalData({...externalData, notes: e.target.value})}
                      rows={3}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                      placeholder="Any additional details about this appointment..."
                    />
                  </div>
                  
                  {/* Action Buttons */}
                  <div className="flex justify-between space-x-3">
                    <button
                      onClick={() => setStep(1)}
                      className="px-6 py-2 bg-gray-200 hover:bg-gray-300 text-gray-700 rounded-lg font-medium"
                    >
                      ← Back
                    </button>
                    <div className="flex space-x-3">
                      <button
                        onClick={onClose}
                        className="px-6 py-2 bg-gray-200 hover:bg-gray-300 text-gray-700 rounded-lg font-medium"
                      >
                        Cancel
                      </button>
                      <button
                        onClick={handleSave}
                        disabled={!appointmentDatetime || saving}
                        className="px-6 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {saving ? 'Saving...' : 'Schedule Appointment'}
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

// Pending Tasks Component

export default AddAppointmentModal;
